import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import PerformanceGraph from '../components/PerformanceGraph';
import ScrollableDropdown from '../components/ScrollableDropdown';

const EmployeeList = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [performanceData, setPerformanceData] = useState(null);
  const [performanceLoading, setPerformanceLoading] = useState(false);
  const [employeeStats, setEmployeeStats] = useState(null);
  const [employeeKpis, setEmployeeKpis] = useState(null);
  const [employeeApplications, setEmployeeApplications] = useState([]);
  const [selectedApplicationId, setSelectedApplicationId] = useState('all');
  const [selectedStatusGroup] = useState('All');

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/api/auth/all-users');
      const filteredEmployees = data.data.filter(u => u.role === 'tester' || u.role === 'developer');
      setEmployees(filteredEmployees);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
      setEmployees([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployeePerformance = async (employeeId, applicationId = 'all', statusGroup = 'All') => {
    try {
      setPerformanceLoading(true);
      const employee = employees.find(emp => emp._id === employeeId);

      const response = await axios.get(`/api/manager/employees/${employeeId}/performance`, {
        params: { applicationId, statusGroup },
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      const { data } = response.data;

      // Set employee stats, KPIs, and applications (only update applications on initial load to persist the filter list)
      setEmployeeStats(data.stats || {});
      setEmployeeKpis(data.kpis || {});
      if (applicationId === 'all' && data.applications) {
        setEmployeeApplications(data.applications);
      }

      // Format data for the graph based on employee role
      const formattedData = data.labels.map((date, index) => {
        const dataPoint = { date };

        if (employee?.role === 'developer') {
          dataPoint['Bugs Assigned'] = data.bugsAssigned[index] || 0;
          dataPoint['Bugs Fixed'] = data.bugsFixed[index] || 0;
        } else {
          dataPoint['Bugs Logged'] = data.bugsLogged[index] || 0;
          dataPoint['Bugs Closed'] = data.bugsClosed[index] || 0;
          dataPoint['Bugs Retested'] = data.bugsRetested[index] || 0;
        }

        return dataPoint;
      });

      setPerformanceData(formattedData);

    } catch (error) {
      console.error('Failed to fetch performance data:', error);
      toast.error('Failed to load performance data');
      setPerformanceData([]);
      setEmployeeStats(null);
      setEmployeeKpis(null);
    } finally {
      setPerformanceLoading(false);
    }
  };

  const handleEmployeeChange = (value) => {
    setSelectedEmployeeId(value);
    setSelectedApplicationId('all'); // Reset application filter

    if (value) {
      const employee = employees.find(emp => emp._id === value);
      setSelectedEmployee(employee);
      fetchEmployeePerformance(value, 'all', selectedStatusGroup);
    } else {
      setSelectedEmployee(null);
      setPerformanceData(null);
      setEmployeeApplications([]);
      setEmployeeKpis(null);
    }
  };

  const handleApplicationChange = (value) => {
    setSelectedApplicationId(value);
    if (selectedEmployeeId) {
      fetchEmployeePerformance(selectedEmployeeId, value, 'All');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
        {/* Header with Dropdown */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">Employee Management</h1>
              <p className="mt-1 text-sm text-gray-600">View and analyze employee performance</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-64">
                <label htmlFor="employee-select" className="block text-sm font-medium text-gray-700 mb-1">
                  Employee
                </label>
                <ScrollableDropdown
                  value={selectedEmployeeId}
                  onChange={handleEmployeeChange}
                  options={[
                    { value: '', label: 'Select Employee' },
                    ...employees.map((employee) => ({
                      value: employee._id,
                      label: employee.name,
                      meta: (
                        <span className="px-2 py-0.5 text-[10px] font-bold uppercase rounded border bg-blue-50 text-blue-600 border-blue-100">
                          {employee.role === 'developer' ? 'Dev' : 'QA'}
                        </span>
                      )
                    }))
                  ]}
                  placeholder="Employee"
                  searchable={true}
                  searchPlaceholder="Search employees..."
                  buttonHeightClassName="h-10"
                  maxMenuHeightPx={300}
                  maxMenuHeightClassName="max-h-80"
                />
              </div>
            </div>
          </div>
        </div>

        {selectedEmployee ? (
          <>
            {/* Two Column Layout: Employee Details (Left) and Key Metrics + Trends (Right) */}
            <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 mb-8">
              {/* Employee Details Card - Left Side (30% width roughly) */}
              <div className="lg:col-span-3">
                <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden h-full">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                    <h2 className="text-lg font-semibold text-gray-900">Employee Details</h2>
                  </div>
                  <div className="p-6">
                    {/* Employee Avatar */}
                    <div className="flex flex-col items-center mb-6">
                      <div className="h-24 w-24 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white text-3xl font-bold shadow-lg mb-3">
                        {selectedEmployee.name.charAt(0).toUpperCase()}
                      </div>
                      <h3 className="text-xl font-semibold text-gray-900">{selectedEmployee.name}</h3>
                      <p className="text-sm text-gray-500">{selectedEmployee.email}</p>
                    </div>

                    {/* Employee Info */}
                    <div className="space-y-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">Designation</label>
                        <p className="mt-1 text-sm font-medium text-gray-900">
                          {selectedEmployee.designation || selectedEmployee.role.charAt(0).toUpperCase() + selectedEmployee.role.slice(1)}
                        </p>
                      </div>

                      {/* Applications List */}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {selectedEmployee.role === 'developer' ? 'Applications Developed' : 'Member of Applications'}
                        </label>
                        <div className="mt-2 space-y-2">
                          {employeeApplications.length > 0 ? (
                            employeeApplications.map((app) => (
                              <div key={app._id} className="flex items-center px-3 py-2 bg-gray-50 rounded-md border border-gray-200">
                                <svg className="h-4 w-4 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="text-sm text-gray-700">{app.name}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-gray-500 italic">No applications assigned</p>
                          )}
                        </div>
                      </div>

                      {/* Total Bugs */}
                      <div className="pt-4 border-t border-gray-200">
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {selectedEmployee.role === 'developer' ? 'Bugs Fixed (Total)' : 'Bugs Logged (Total)'}
                        </label>
                        <p className="mt-1 text-2xl font-bold text-indigo-600">
                          {selectedEmployee.role === 'developer' ? (employeeStats?.bugsFixed || 0) : (employeeStats?.bugsLogged || 0)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Analytical Section - Right Side (70% width roughly) */}
              <div className="lg:col-span-7 space-y-6">
                {/* Key Metrics */}
                <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                    <div className="flex items-center justify-between">
                      <h2 className="text-lg font-semibold text-gray-900">Key Metrics</h2>
                      {/* Application Filter */}
                      {employeeApplications.length > 0 && (
                        <div className="w-64">
                          <ScrollableDropdown
                            value={selectedApplicationId}
                            onChange={handleApplicationChange}
                            options={[
                              { value: 'all', label: 'All Applications' },
                              ...employeeApplications.map((app) => ({ value: app._id, label: app.name }))
                            ]}
                            placeholder="Filter by application"
                            searchable={true}
                            searchPlaceholder="Search applications..."
                            buttonHeightClassName="h-9"
                            maxMenuHeightPx={200}
                            maxMenuHeightClassName="max-h-52"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="p-6">
                    {performanceLoading ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : employeeKpis ? (
                      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                        <KPICard
                          title="Open"
                          count={employeeKpis.open || 0}
                          color="text-blue-600"
                          border="border-blue-100"
                          bg="bg-blue-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Active'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Active'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="In Progress"
                          count={employeeKpis.inProgress || 0}
                          color="text-amber-600"
                          border="border-amber-100"
                          bg="bg-amber-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Active'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Active'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Reopened"
                          count={employeeKpis.reopened || 0}
                          color="text-orange-600"
                          border="border-orange-100"
                          bg="bg-orange-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Active'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Active'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Fixed"
                          count={employeeKpis.fixed || 0}
                          color="text-emerald-600"
                          border="border-emerald-100"
                          bg="bg-emerald-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Completed'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Completed'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Closed"
                          count={employeeKpis.closed || 0}
                          color="text-indigo-600"
                          border="border-indigo-100"
                          bg="bg-indigo-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Completed'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Completed'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Duplicate"
                          count={employeeKpis.duplicate || 0}
                          color="text-purple-600"
                          border="border-purple-100"
                          bg="bg-purple-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Rejected"
                          count={employeeKpis.rejected || 0}
                          color="text-red-600"
                          border="border-red-100"
                          bg="bg-red-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Deferred"
                          count={employeeKpis.deferred || 0}
                          color="text-yellow-600"
                          border="border-yellow-100"
                          bg="bg-yellow-50"
                          isHighlighted={selectedStatusGroup === 'All'}
                          isDimmed={selectedStatusGroup !== 'All'}
                        />
                        <KPICard
                          title="Not a Bug"
                          count={employeeKpis.notABug || 0}
                          color="text-pink-600"
                          border="border-pink-100"
                          bg="bg-pink-50"
                          isHighlighted={selectedStatusGroup === 'All' || ['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                        />
                        <KPICard
                          title="Deprecated"
                          count={employeeKpis.deprecated || 0}
                          color="text-gray-500"
                          border="border-gray-200"
                          bg="bg-gray-100"
                          isHighlighted={selectedStatusGroup === 'All' || ['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                          isDimmed={selectedStatusGroup !== 'All' && !['Invalid / Not Actionable'].includes(selectedStatusGroup)}
                        />
                      </div>
                    ) : (
                      <div className="text-center text-gray-500 py-8">
                        No KPI data available
                      </div>
                    )}
                  </div>
                </div>

                {/* Performance Trend Section */}
                <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-indigo-50 to-blue-50">
                    <h2 className="text-lg font-semibold text-gray-900">Performance Trend</h2>
                    <p className="text-sm text-gray-600 mt-1">
                      30-day activity trend
                      {selectedApplicationId !== 'all' && employeeApplications.length > 0 && (
                        <span className="ml-2 text-indigo-600 font-medium">
                          ({employeeApplications.find(app => app._id === selectedApplicationId)?.name || 'Selected Application'})
                        </span>
                      )}
                    </p>
                  </div>
                  <div className="p-6">
                    {performanceLoading ? (
                      <div className="flex items-center justify-center h-64">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                      </div>
                    ) : performanceData && performanceData.length > 0 ? (
                      <PerformanceGraph data={performanceData} />
                    ) : (
                      <div className="text-center text-gray-500 py-16">
                        <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                        <p className="text-base font-medium">No performance data available</p>
                        <p className="text-sm mt-1">Data will appear once the employee has activity</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </>

        ) : (
          /* Enhanced Empty State */
          <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-8 py-16 text-center">
              {/* Icon with subtle animation */}
              <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-blue-50 to-indigo-50 mb-6 animate-pulse">
                <svg
                  className="h-10 w-10 text-indigo-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
              </div>

              {/* Title */}
              <h3 className="text-xl font-semibold text-gray-900 mb-2">
                No Employee Selected
              </h3>

              {/* Main instruction */}
              <p className="text-base text-gray-600 mb-8 max-w-md mx-auto">
                Please select an employee from the dropdown above to view their performance analytics and activity trends.
              </p>

              {/* Helpful tips section */}
              <div className="bg-blue-50 border border-blue-100 rounded-lg p-6 max-w-2xl mx-auto mb-6">
                <div className="flex items-start">
                  <svg className="h-5 w-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="text-left">
                    <h4 className="text-sm font-medium text-blue-900 mb-2">Quick Tips:</h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      <li>• You can search employees by name or role in the dropdown</li>
                      <li>• View 30-day performance trends and detailed analytics</li>
                      <li>• Track bugs logged, fixed, and retested by each employee</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Visual indicators */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto mt-8">
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg p-4 border border-green-100">
                  <div className="text-green-600 font-semibold text-sm mb-1">Testers</div>
                  <div className="text-xs text-green-700">Track bugs logged & retested</div>
                </div>
                <div className="bg-gradient-to-br from-purple-50 to-indigo-50 rounded-lg p-4 border border-purple-100">
                  <div className="text-purple-600 font-semibold text-sm mb-1">Developers</div>
                  <div className="text-xs text-purple-700">Monitor bugs assigned & fixed</div>
                </div>
                <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-lg p-4 border border-orange-100">
                  <div className="text-orange-600 font-semibold text-sm mb-1">Analytics</div>
                  <div className="text-xs text-orange-700">30-day performance trends</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// KPI Card Component - Matching Manager Dashboard Design
const KPICard = ({ title, count, color, border, bg, isHighlighted, isDimmed }) => (
  <div className={`p-3 rounded-lg border ${border} ${bg} shadow-sm flex flex-col justify-between h-20 transition-all ${isDimmed ? 'opacity-30 grayscale-[0.5]' : 'opacity-100'} ${isHighlighted && !isDimmed ? 'ring-2 ring-offset-1 ring-indigo-500 shadow-md scale-[1.02]' : 'hover:shadow-md'}`}>
    <p className={`text-[10px] font-bold uppercase tracking-wider opacity-70 ${color === 'text-gray-900' ? 'text-gray-500' : color}`}>{title}</p>
    <div className="flex items-end justify-between">
      <span className={`text-2xl font-bold leading-none ${color}`}>{count}</span>
      {/* Visual indicator bar */}
      <div className={`w-8 h-1 rounded-full opacity-30 ${color.replace('text-', 'bg-')}`}></div>
    </div>
  </div>
);

export default EmployeeList;
